(() => {
  'use strict';
  const safeUrl = (value) => {
    if (!value) return '';
    try { const url = new URL(value, location.origin); return ['https:', 'http:'].includes(url.protocol) ? url.href : ''; } catch { return ''; }
  };
  const make = (tag, cls, text) => { const node = document.createElement(tag); if (cls) node.className = cls; if (text != null) node.textContent = text; return node; };
  const searchNumbers = value => [...new Set(String(value || '').match(/\b\d{4,7}\b/g) || [])];
  const searchTags = product => Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',').map(tag => tag.trim());
  const searchKind = product => {
    const tags = searchTags(product), title = String(product.title || '');
    if (tags.includes('fb-nameplates') || /name\s?plate/i.test(title)) return {key:'nameplate', label:'LED nameplate', rank:1};
    if (tags.includes('fb-wallmount') || /wall[\s-]?mount/i.test(title)) return {key:'mount', label:'Wall mount', rank:3};
    if (/^Light Kit for /i.test(title) || /light kit/i.test(product.type || '')) return {key:'kit', label:'Light kit', rank:0};
    return {key:'accessory', label:'Accessory', rank:2};
  };
  const searchImage = product => {
    for (const image of [product.featured_image, product.image]) {
      const value = typeof image === 'string' ? image : image?.url || image?.src;
      if (value && safeUrl(value)) return safeUrl(value);
    }
    return '';
  };
  const searchVariant = product => {
    const variantId = new URL(safeUrl(product.url) || location.origin).searchParams.get('variant');
    return variantId ? (product.variants || []).find(variant => String(variant.id) === variantId) : null;
  };
  const searchAvailable = product => (searchVariant(product)?.available ?? product.available) !== false;
  class FireflySearch extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('.ff-suggestions');
      if (!this.input || !this.panel) return;
      this.initialized = true;
      this.list = this.panel.querySelector('[role="listbox"]');
      this.status = this.panel.querySelector('[role="status"]');
      this.active = -1;
      this.cache = new Map();
      this.input.addEventListener('input', event => {
        clearTimeout(this.timer); this.controller?.abort(); this.close();
        if (!event.isComposing) this.queue();
      });
      this.input.addEventListener('compositionend', () => this.queue());
      this.input.addEventListener('focus', () => { if (this.input.value.trim().length >= 2) this.queue(); });
      this.input.addEventListener('keydown', event => this.onKey(event));
      this.addEventListener('focusout', () => setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); }, 0));
      this.querySelector('form').addEventListener('submit', () => { clearTimeout(this.timer); this.controller?.abort(); this.close(); });
    }
    queue() {
      clearTimeout(this.timer);
      const term = this.input.value.trim();
      if (term.length < 2) return;
      this.timer = setTimeout(() => this.suggest(term), 200);
    }
    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.input.removeAttribute('aria-busy');
      this.active = -1;
      this.list.querySelectorAll('[role="option"]').forEach(option => option.setAttribute('aria-selected', 'false'));
    }
    onKey(event) {
      if (event.isComposing) return;
      if (event.key === 'Escape') {
        if (!this.panel.hidden) { event.preventDefault(); event.stopPropagation(); }
        clearTimeout(this.timer); this.controller?.abort(); this.close(); return;
      }
      const options = [...this.list.querySelectorAll('[role="option"]')];
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !this.panel.hidden && options.length) {
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        this.active = this.active < 0 ? (direction > 0 ? 0 : options.length - 1) : (this.active + direction + options.length) % options.length;
        options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.active)));
        this.input.setAttribute('aria-activedescendant', options[this.active].id);
        options[this.active].scrollIntoView({block:'nearest'});
      } else if (event.key === 'Enter' && !this.panel.hidden && this.active >= 0 && options[this.active]) {
        event.preventDefault(); options[this.active].click(); this.close();
      }
    }
    formatPrice(product) {
      const variant = searchVariant(product);
      const raw = variant?.price ?? product.price_min ?? product.price;
      if (raw === null || raw === undefined || raw === '' || !Number.isFinite(Number(raw))) return '';
      const amount = Number(raw);
      const varies = !variant && Number(product.price_max) > amount;
      const currency = this.dataset.currency;
      if (!/^[A-Z]{3}$/.test(currency || '')) return '';
      try { return `${varies ? 'From ' : ''}${new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'en', {style:'currency', currency}).format(amount)}`; }
      catch { return `${varies ? 'From ' : ''}${currency} ${amount.toFixed(2)}`; }
    }
    render(products, term, failed = false) {
      this.list.replaceChildren();
      this.active = -1;
      this.input.removeAttribute('aria-activedescendant');
      products.forEach((product, index) => {
        const li = make('li'); li.setAttribute('role', 'presentation');
        const link = make('a', 'ff-suggestion-product'); link.href = safeUrl(product.url);
        link.id = `${this.list.id}-${index}`;
        link.setAttribute('role', 'option'); link.setAttribute('aria-selected', 'false'); link.tabIndex = -1;
        const source = searchImage(product);
        if (source) { const image = make('img'); image.src = source; image.alt = ''; image.width = 52; image.height = 52; image.loading = 'lazy'; image.addEventListener('error', () => image.hidden = true, {once:true}); link.append(image); }
        const content = make('span', 'ff-suggestion-content');
        const kind = searchKind(product);
        const numbers = searchNumbers(`${product.title} ${searchTags(product).join(' ')}`);
        content.append(make('span', 'ff-suggestion-kind', `${kind.label}${numbers.length ? ` · Set ${numbers.join(' / ')}` : ''}`));
        content.append(make('span', 'ff-suggestion-title', product.title));
        const variant = searchVariant(product);
        if (variant?.title && variant.title !== 'Default Title') content.append(make('span', 'ff-suggestion-version', variant.title));
        const detail = make('span', 'ff-suggestion-detail');
        const price = this.formatPrice(product); if (price) detail.append(make('span', 'ff-suggestion-price', price));
        detail.append(make('span', `ff-suggestion-stock${searchAvailable(product) ? '' : ' is-sold-out'}`, searchAvailable(product) ? 'Available' : 'Sold out'));
        content.append(detail); link.append(content); li.append(link); this.list.append(li);
      });
      const numberQuery = searchNumbers(term).length > 0;
      this.status.textContent = failed ? 'Search suggestions are unavailable. You can still browse the full directory.' : products.length ? `${products.length} ${numberQuery ? 'matching products for this set' : 'matching products'}` : numberQuery ? 'No exact match for that set number. Check the number on your LEGO® box or ask us for help.' : 'No matches yet. Try a set number or browse the collection.';
      this.panel.querySelector('.ff-suggestion-all').href = `${this.dataset.root}search?view=kits&type=product&q=${encodeURIComponent(term)}`;
      this.panel.querySelector('.ff-suggestion-all').textContent = products.length ? 'View all results →' : 'Search the full directory →';
      this.panel.querySelector('.ff-suggestion-help').hidden = products.length > 0 && !failed;
      this.input.removeAttribute('aria-busy');
      this.input.setAttribute('aria-expanded', 'true');
      this.panel.hidden = false;
    }
    async suggest(term) {
      if (this.input.value.trim() !== term || !this.contains(document.activeElement)) return;
      this.controller?.abort();
      const controller = new AbortController(); this.controller = controller;
      const cached = this.cache.get(term.toLowerCase());
      if (cached && Date.now() - cached.time < 60000) { this.render(cached.products, term); return; }
      this.input.setAttribute('aria-busy', 'true');
      const url = new URL(`${this.dataset.root}search/suggest.json`, location.origin);
      url.search = new URLSearchParams({q:term, 'resources[type]':'product', 'resources[limit]':'10', 'resources[options][unavailable_products]':'last'});
      try {
        const response = await fetch(url, {signal:controller.signal});
        if (!response.ok) throw new Error('Search unavailable');
        const data = await response.json();
        if (controller.signal.aborted || this.input.value.trim() !== term || !this.contains(document.activeElement)) return;
        const requestedNumbers = searchNumbers(term);
        const candidates = Array.isArray(data.resources?.results?.products) ? data.resources.results.products : [];
        const products = [...new Map(candidates.filter(product => {
          if (!safeUrl(product.url) || /membership/i.test(product.type || '')) return false;
          const numbers = searchNumbers(`${product.title} ${product.handle} ${searchTags(product).join(' ')} ${(product.variants || []).map(variant => `${variant.title || ''} ${variant.sku || ''}`).join(' ')}`);
          return requestedNumbers.every(number => numbers.includes(number));
        }).map(product => [product.id || product.url, product])).values()].sort((a, b) => Number(searchAvailable(b)) - Number(searchAvailable(a)) || searchKind(a).rank - searchKind(b).rank);
        this.cache.set(term.toLowerCase(), {time:Date.now(), products});
        if (this.cache.size > 30) this.cache.delete(this.cache.keys().next().value);
        this.render(products, term);
      } catch (error) {
        if (error.name !== 'AbortError' && this.input.value.trim() === term && this.contains(document.activeElement)) this.render([], term, true);
      }
    }
    disconnectedCallback() { this.controller?.abort(); clearTimeout(this.timer); }
  }

  const catalogs = new Map();
  function getCatalog(root) {
    const key = `${root}|${document.documentElement.lang}|${window.Shopify?.currency?.active || ''}`;
    if (!catalogs.has(key)) catalogs.set(key, {root, products:new Map(), next:1, total:1, loading:false, complete:false, error:null, listeners:new Set()});
    return catalogs.get(key);
  }
  async function loadCatalog(state) {
    if (state.loading || state.complete) return;
    const notify = () => state.listeners.forEach(listener => listener(state));
    state.loading = true; state.error = null; notify();
    try {
      while (state.next <= state.total) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 20000);
        let data;
        try {
          const response = await fetch(`${state.root}collections/all?view=firefly-index&page=${state.next}`, {signal:controller.signal});
          if (!response.ok) throw new Error('Catalog unavailable');
          data = await response.json();
          if (!Array.isArray(data.products) || !Number.isInteger(data.pages) || data.pages < 0 || data.pages > 100 || data.page !== state.next) throw new Error('Invalid catalog');
        } finally { clearTimeout(timeout); }
        for (const product of data.products) {
          if (!product.id || typeof product.title !== 'string' || !safeUrl(product.url) || /membership/i.test(product.type || '')) continue;
          const tags = searchTags(product);
          const numbers = searchNumbers(`${product.title} ${tags.filter(tag => /^\d{4,7}$/.test(tag)).join(' ')}`);
          const kind = searchKind(product).key;
          state.products.set(product.id, {...product, tags, numbers, kind, searchable:`${product.title} ${numbers.join(' ')}`.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()});
        }
        state.total = Math.max(1, data.pages); state.next += 1;
        state.complete = state.next > state.total;
        notify();
      }
    } catch (error) { state.error = error; }
    finally { state.loading = false; notify(); }
  }
  class FireflyDirectory extends HTMLElement {
    connectedCallback() {
      if(this.initialized) { this.connectCatalog(); window.addEventListener('popstate',this.restore); return; } this.initialized=true;
      this.form=this.querySelector('form'); this.query=this.form.querySelector('[name="q"]');
      this.availableFilter=this.querySelector('[name="available"]');this.budgetFilter=this.querySelector('[name="max_price"]');
      this.results=this.querySelector('.ff-directory-results'); this.status=this.querySelector('[role="status"]'); this.more=this.querySelector('.ff-load-more');
      this.form.addEventListener('submit',event=>{event.preventDefault();this.apply();});
      this.query.addEventListener('input',()=>{clearTimeout(this.timer);this.timer=setTimeout(()=>this.apply(),150);});
      this.querySelectorAll('select').forEach(select=>select.addEventListener('change',()=>this.apply()));
      this.availableFilter?.addEventListener('change',()=>this.apply());
      this.budgetFilter?.addEventListener('input',()=>{clearTimeout(this.budgetTimer);this.budgetTimer=setTimeout(()=>this.apply(),150);});
      this.budgetFilter?.addEventListener('change',()=>this.apply());
      this.querySelector('.ff-reset').addEventListener('click',()=>{this.query.value='';this.querySelectorAll('select').forEach(select=>select.selectedIndex=0);if(this.availableFilter)this.availableFilter.checked=false;if(this.budgetFilter)this.budgetFilter.value='';const url=new URL(location.href);['available','max_price','kind'].forEach(key=>url.searchParams.delete(key));history.replaceState(null,'',url);this.apply();this.query.focus();});
      this.more.addEventListener('click',()=>{this.limit+=24;this.render(true);});
      this.querySelector('.ff-directory-error button').addEventListener('click',()=>this.load());
      this.restore = () => {
        const params = new URL(location.href).searchParams;
        this.query.value = params.get('q') || '';
        if(this.availableFilter)this.availableFilter.checked=params.get('available')==='1';
        if(this.budgetFilter)this.budgetFilter.value=params.get('max_price') || '';
        this.querySelectorAll('select').forEach(select => {
          const value = params.get(select.name);
          select.value = [...select.options].some(option => option.value === value) ? value : select.options[0].value;
        });
        this.apply(false);
      };
      window.addEventListener('popstate',this.restore);
      this.restore(); this.connectCatalog();
    }
    disconnectedCallback(){clearTimeout(this.timer);clearTimeout(this.budgetTimer);window.removeEventListener('popstate',this.restore);this.catalog?.listeners.delete(this.onCatalog);}
    connectCatalog(){
      this.catalog = getCatalog(this.dataset.root);
      this.onCatalog ||= state => {
        this.products = [...state.products.values()];
        this.complete = state.complete; this.loading = state.loading && !state.complete; this.loadError = state.error;
        const error = this.querySelector('.ff-directory-error');
        error.hidden = !state.error;
        error.querySelector('p').textContent = this.products.length ? 'Some products could not be loaded. Your current results and filters are saved. Try again to finish loading.' : "We couldn't load the collection. Please try again.";
        this.apply(false, true);
      };
      this.catalog.listeners.add(this.onCatalog); this.onCatalog(this.catalog); this.load();
    }
    load(){ loadCatalog(this.catalog); }
    readBudget(){
      const raw=this.budgetFilter?.value.trim() || '';
      const valid=!raw||(/^\d+(?:\.\d{1,2})?$/.test(raw)&&Number.isSafeInteger(Math.round(Number(raw)*100)));
      if(this.budgetFilter){
        this.budgetFilter.setAttribute('aria-invalid',String(!valid));
        const error=this.querySelector('.ff-budget-error');
        if(error){error.hidden=valid;error.textContent=valid?'':'Enter a valid price, such as 49.99, with no currency symbol. The price limit is not applied.';}
      }
      return valid&&raw?Math.round(Number(raw)*100):null;
    }
    orderPrice(product){return product.available&&Number.isFinite(product.availablePriceValue)&&product.availablePriceValue>=0?product.availablePriceValue:null;}
    showFilters(theme,kind,available,budget){
      const container=this.querySelector('.ff-active-filters');if(!container)return;
      const labels=[];const guides=this.dataset.mode==='guides';
      if(this.query.value.trim())labels.push(`Search: ${this.query.value.trim()}`);
      if(theme)labels.push(this.querySelector('[name="theme"]').selectedOptions[0].textContent);
      if(!guides)labels.push(this.querySelector('[name="kind"]').selectedOptions[0].textContent);
      if(available)labels.push('Available to order');
      if(budget!==null){let amount=`${this.dataset.currency || ''} ${(budget/100).toFixed(2)}`;try{amount=new Intl.NumberFormat(document.documentElement.lang || 'en',{style:'currency',currency:this.dataset.currency}).format(budget/100);}catch{}labels.push(`Up to ${amount}`);}
      if(!labels.length)labels.push('All installation guides');
      container.replaceChildren(...labels.map(label=>make('span','ff-filter-tag',label)));
    }
    apply(updateUrl=true,keepLimit=false){
      if(!this.products)return;
      const text=this.query.value.trim().normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLowerCase();
      const terms=text.split(/\s+/).filter(Boolean); const theme=this.querySelector('[name="theme"]').value;
      const guides=this.dataset.mode==='guides'; const kind=this.querySelector('[name="kind"]')?.value || 'all';
      const available=!guides&&!!this.availableFilter?.checked;const budget=guides?null:this.readBudget();
      this.filtered=this.products.filter(product=>(!guides||!!safeUrl(product.guide))&&(guides||kind==='all'||product.kind===kind)&&(!theme||product.tags.includes(theme))&&(!available||product.available===true)&&(budget===null||(this.orderPrice(product)!==null&&this.orderPrice(product)<=budget))&&terms.every(term=>/^\d{4,7}$/.test(term)?product.numbers.includes(term):product.searchable.includes(term)));
      const sort=this.querySelector('[name="sort"]').value;
      this.filtered.sort((a,b)=>{
        if(sort==='price-asc')return (this.orderPrice(a)??a.priceValue)-(this.orderPrice(b)??b.priceValue);
        if(sort==='price-desc')return (this.orderPrice(b)??b.priceValue)-(this.orderPrice(a)??a.priceValue);
        if(sort==='number')return Number(a.numbers[0]||99999999)-Number(b.numbers[0]||99999999);
        if(sort==='relevance'&&text){const score=p=>(p.numbers.includes(text)?100:0)+(p.kind==='kit'?10:0)+(p.searchable.startsWith(text)?5:0);const diff=score(b)-score(a);if(diff)return diff;}
        return a.title.localeCompare(b.title);
      });
      if(updateUrl){
        const url=new URL(location.href);if(text)url.searchParams.set('q',this.query.value.trim());else url.searchParams.delete('q');
        this.querySelectorAll('select').forEach(select => {if(select.selectedIndex > 0)url.searchParams.set(select.name,select.value);else url.searchParams.delete(select.name);});
        if(!guides){if(available)url.searchParams.set('available','1');else url.searchParams.delete('available');if(budget!==null)url.searchParams.set('max_price',String(budget/100));else url.searchParams.delete('max_price');}
        url.searchParams.set('view',this.dataset.mode);url.searchParams.set('type','product');history.replaceState(null,'',url);
      }
      document.querySelectorAll('.ff-directory-tabs a').forEach(link=>{
        const url=new URL(link.href);
        if(this.query.value.trim())url.searchParams.set('q',this.query.value.trim());else url.searchParams.delete('q');
        if(theme)url.searchParams.set('theme',theme);else url.searchParams.delete('theme');
        const current=new URL(location.href).searchParams;
        const savedAvailable=guides?current.get('available')==='1':available;
        const savedBudget=guides?current.get('max_price'):budget!==null?String(budget/100):null;
        const savedKind=guides?current.get('kind'):kind;
        if(savedAvailable)url.searchParams.set('available','1');else url.searchParams.delete('available');
        if(savedBudget!==null)url.searchParams.set('max_price',savedBudget);else url.searchParams.delete('max_price');
        if(savedKind&&savedKind!=='kit')url.searchParams.set('kind',savedKind);else url.searchParams.delete('kind');
        if(sort!=='relevance'&&(url.searchParams.get('view')!=='guides'||!sort.startsWith('price-')))url.searchParams.set('sort',sort);else url.searchParams.delete('sort');
        link.href=url.href;
      });
      this.showFilters(theme,kind,available,budget);
      if(!keepLimit || !this.limit)this.limit=24;this.render();
    }
    render(append=false){
      const focusedHref=this.results.contains(document.activeElement)?document.activeElement.href:null;
      const start=append?this.results.children.length:0;if(!append)this.results.replaceChildren();
      const fragment=document.createDocumentFragment();const guides=this.dataset.mode==='guides';
      this.filtered.slice(start,this.limit).forEach(product=>{
        const card=make('article','ff-product-card');const link=make('a','ff-product-image');link.href=safeUrl(product.url);
        if(safeUrl(product.image)){const image=make('img');image.src=safeUrl(product.image);image.alt=product.title;image.loading='lazy';image.width=540;image.height=540;link.append(image);}
        card.append(link);const meta=make('div','ff-product-meta');meta.append(make('span','',product.numbers.length?`SET ${product.numbers.join(' / ')}`:product.kind==='accessory'?'LIGHTING ACCESSORY':'FIREFLY BRICKS'));if(product.guide)meta.append(make('span','','PDF guide'));card.append(meta);
        const title=make('h3');const titleLink=make('a','',product.title.replace(/^Light Kit for LEGO[®]?\s*/i,''));titleLink.href=safeUrl(product.url);title.append(titleLink);card.append(title);
        if(!guides && Number.isInteger(product.selectedReviewCount) && product.selectedReviewCount>0){const reviews=make('a','ff-card-reviews',`${product.selectedReviewCount} selected 4–5-star reviews`);const target=new URL(safeUrl(product.url));target.hash=`ff-product-reviews-${product.id}`;reviews.href=target.href;card.append(reviews);}
        if(guides){const pdf=make('a','ff-directory-pdf','View installation PDF ↗');pdf.href=safeUrl(product.guide);pdf.target='_blank';pdf.rel='noopener';pdf.setAttribute('aria-label',`View PDF for ${product.title} (opens in a new tab)`);card.append(pdf);}else{const hasOrderPrice=this.orderPrice(product)!==null&&product.availablePrice;const price=make('p','ff-card-price',`${(hasOrderPrice?product.availablePriceVaries:product.priceVaries)?'From ':''}${hasOrderPrice?product.availablePrice:product.price}`);if(!product.available)price.append(make('span','','Sold out'));card.append(price);}
        fragment.append(card);
      });
      this.results.append(fragment);this.results.setAttribute('aria-busy',String(this.loading&&!this.results.children.length));
      const count=this.filtered.length;
      const matches=`${count} ${guides?'installation guide':'product'}${count===1?'':'s'}${this.query.value.trim()?` matching “${this.query.value.trim()}”`:''}`;
      this.status.textContent=this.complete?matches:this.loadError?`${matches} loaded. Loading stopped — try again below.`:this.products.length?`${matches} loaded · Loading more products…`:'Loading the collection…';
      this.querySelector('.ff-directory-empty').hidden=count!==0||!this.complete;this.more.hidden=count<=this.limit;this.more.textContent=`Show more (${Math.max(0,count-this.limit)} ${this.complete?'remaining':'loaded'})`;
      if(focusedHref&&!append)[...this.results.querySelectorAll('a')].find(link=>link.href===focusedHref)?.focus({preventScroll:true});
      if(append)this.results.children[start]?.querySelector('a')?.focus();
    }
  }
  if(!customElements.get('ff-search'))customElements.define('ff-search',FireflySearch);
  if(!customElements.get('ff-directory'))customElements.define('ff-directory',FireflyDirectory);
  class FireflyProductNav extends HTMLElement {
    connectedCallback(){
      if(this.initialized)return;
      this.initialize=()=>{
        if(!this.isConnected||this.initialized)return;
        this.initialized=true;
        this.entries=[...this.querySelectorAll('[data-section-key]')].map(link=>{
          const target=document.getElementById(`shopify-section-${this.dataset.sectionPrefix}__${link.dataset.sectionKey}`);
          if(!target||!target.querySelector('h1,h2,h3,button,a')||!target.getClientRects().length){link.remove();return null;}
          link.href=`#${target.id}`;target.classList.add('ff-product-nav-target');return {link,target};
        }).filter(Boolean);
        if(this.entries.length<2)return;
        this.hidden=false;
        this.onClick=event=>{
          const entry=this.entries.find(item=>item.link===event.target.closest('a'));
          if(!entry||event.button!==0||event.metaKey||event.ctrlKey||event.shiftKey||event.altKey)return;
          event.preventDefault();
          const heading=entry.target.querySelector('h1,h2,h3')||entry.target;
          if(!heading.hasAttribute('tabindex'))heading.setAttribute('tabindex','-1');
          heading.focus({preventScroll:true});
          entry.target.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth',block:'start'});
          history.replaceState(history.state,'',entry.link.hash);
        };
        this.addEventListener('click',this.onClick);
        this.update=()=>{
          this.frame=0;
          const height=this.closest('.ff-product-nav-section').getBoundingClientRect().height;
          document.documentElement.style.setProperty('--ff-product-nav-height',`${height}px`);
          document.documentElement.style.setProperty('--ff-sticky-footer-space',`${document.querySelector('sticky-atc')?.getBoundingClientRect().height||0}px`);
          let active=this.entries[0];
          for(const entry of this.entries){if(entry.target.getBoundingClientRect().top<=height+48)active=entry;}
          this.entries.forEach(entry=>{if(entry===active)entry.link.setAttribute('aria-current','location');else entry.link.removeAttribute('aria-current');});
        };
        this.onScroll=()=>{if(!this.frame)this.frame=requestAnimationFrame(this.update);};
        addEventListener('scroll',this.onScroll,{passive:true});
        this.resize=new ResizeObserver(this.onScroll);this.resize.observe(this);
        const sticky=document.querySelector('sticky-atc');if(sticky)this.resize.observe(sticky);
        this.update();
      };
      if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',this.initialize,{once:true});else this.initialize();
    }
    disconnectedCallback(){document.removeEventListener('DOMContentLoaded',this.initialize);removeEventListener('scroll',this.onScroll);cancelAnimationFrame(this.frame);this.resize?.disconnect();this.removeEventListener('click',this.onClick);this.initialized=false;}
  }
  if(!customElements.get('ff-product-nav'))customElements.define('ff-product-nav',FireflyProductNav);
  document.addEventListener('click',event=>{const link=event.target.closest('.ff-cart[data-cart-drawer-trigger]');if(!link||event.defaultPrevented)return;const drawer=document.querySelector('cart-drawer');if(drawer&&typeof drawer.open==='function'){event.preventDefault();drawer.open(link);}});
  document.addEventListener('keydown',event=>{if(event.key==='Escape')document.querySelectorAll('.ff-nav-disclosure[open],.ff-mobile-nav[open]').forEach(menu=>{menu.open=false;menu.querySelector('summary').focus();});});
  document.addEventListener('click',event=>{document.querySelectorAll('.ff-nav-disclosure[open],.ff-mobile-nav[open]').forEach(menu=>{if(!menu.contains(event.target))menu.open=false;});});
})();
